import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import { useAuth, type AuthContextProps, type ErrorContext } from 'react-oidc-context';
import { Outlet } from 'react-router';

interface RequireAuthProps {
  /**
   * Exact Cognito group name required to access the route.
   * If absent, any authenticated user may access the route.
   */
  requiredRole?: string;
  /**
   * Cognito group prefix: user must have at least one group starting with this prefix.
   * Example: `requiredRolePrefix="staff:"` — any staff:* group is sufficient.
   * Takes precedence over requiredRole if both are set.
   */
  requiredRolePrefix?: string;
  /**
   * Any-of list of exact Cognito group names: the user must hold at least one.
   * Example: `requiredRoles={['staff:food-ops', 'staff:admin']}` — the app's own role plus
   * `staff:admin` as the platform-wide superset. Mirrors the server-side `@HasRole(...)`
   * any-of semantics so a route and its API agree.
   *
   * Precedence: requiredRolePrefix > requiredRoles > requiredRole. An empty array fails
   * closed (nobody passes) rather than degrading to "any authenticated user".
   */
  requiredRoles?: readonly string[];
  /**
   * What to render while the OIDC session is still being RESOLVED. Omitted — the default for every
   * app — renders the centred spinner, unchanged.
   *
   * The pantry admin suite passes its `AppShell` here so the chrome does not vanish on every
   * cross-app navigation: the OIDC session is restored from storage on every new document, so each
   * new document begins in `isLoading` while that restore is in flight, and discarding the whole
   * shell for that window is what makes suite navigation feel jarring.
   *
   * This covers "still determining" ONLY. A CONFIRMED-anonymous visitor still renders nothing and is
   * redirected — chrome must never reach someone who is not signed in.
   *
   * Must be an element (or omitted), not a conditional expression. `pending={cond && <Shell/>}`
   * renders NOTHING when `cond` is false — `ReactNode` includes `false`, and `pending ?? <Spinner/>`
   * only falls back on `null`/`undefined`, not `false`.
   */
  pending?: ReactNode;
  /**
   * Side effect to run immediately before the denied page's sign-out redirect.
   *
   * Exists because sign-out in the pantry suite is a PAIR, not a single call: four apps clear the
   * cached nav tree and then redirect, and `useRegistryTree` documents the clear as the sign-out
   * half of a two-directional contract. The tree lives in `sessionStorage`, is role-filtered and
   * user-specific, so skipping the clear lets the next staff user in the same tab inherit the
   * previous user's nav — and a denied user is exactly the wrong-role-at-the-keyboard case the
   * contract exists for.
   *
   * A prop rather than a direct `clearRegistryTreeCache()` import because that cache lives in
   * `@nrx/ui`, which sits ABOVE this package; importing it here would invert the layering this
   * package is careful to keep. Apps pass `beforeSignOut={clearRegistryTreeCache}` — a plain
   * function reference, so it works from a module-scope router where no hook is in scope.
   */
  beforeSignOut?: (() => void) | undefined;
}

/**
 * LoadingSpinner — shown while the OIDC session is being restored.
 *
 * @remarks Styled from `@nrx/auth-ui/auth-ui.css`, not Tailwind utilities: Tailwind v4 skips
 * `node_modules` during source detection and workspace packages resolve through it, so a utility
 * written here reached a bundle only where the consuming app's own source happened to use the same
 * class. This spinner rendered as a 0x0 unstyled div in the apps that did not — fx-portal's bundle
 * carried none of its classes — and by coincidence looked right in the ones that did.
 */
function LoadingSpinner(): JSX.Element {
  return (
    // `aria-busy` as well as `role="status"`: the role says a status exists, `aria-busy` is what
    // says the region is still pending. The ring is `aria-hidden` because it is a decorative
    // element with no accessible name inside a live region — assistive tech may otherwise
    // announce it alongside the label. Both mirror `Spinner` in @nrx/ui.
    <div role="status" aria-busy="true" aria-label="Loading..." className="nrx-auth-centered">
      <div className="nrx-auth-spinner" aria-hidden="true" />
    </div>
  );
}

export interface ForbiddenPageProps {
  /** See `RequireAuth`'s `beforeSignOut` — same contract, same reason. */
  beforeSignOut?: (() => void) | undefined;
}

/**
 * ForbiddenPage — shown when a user lacks the required role for a route.
 * Renders a non-technical message with no PHI or internal route details.
 *
 * @remarks Offers sign-out because this is otherwise a dead end. The page renders outside any
 * app shell, so it carries no nav and no session controls — a staff member who reaches a tool
 * they lack the role for has no in-app way to switch accounts, and recovering means
 * hand-constructing a Cognito `/logout` URL.
 */
export function ForbiddenPage({ beforeSignOut }: ForbiddenPageProps = {}): JSX.Element {
  const auth = useAuth();
  return (
    <div role="main" className="nrx-auth-centered">
      <div className="nrx-auth-forbidden">
        <h1 className="nrx-auth-forbidden__title">Access denied</h1>
        <p className="nrx-auth-forbidden__body">
          You don&apos;t have permission to view this page.
          <br />
          Contact{' '}
          <a href="mailto:support@nourishedrx.com" className="nrx-auth-forbidden__link">
            NourishedRx support
          </a>{' '}
          if you believe this is an error.
        </p>
        <div className="nrx-auth-forbidden__actions">
          <button
            type="button"
            className="nrx-auth-forbidden__signout"
            onClick={() => {
              // BEFORE the redirect, not after: `signoutRedirect` navigates away, so anything
              // queued behind it may never run. `apps/marketing-outreach-upload` documents the
              // same ordering as mandatory — "drop the cached nav tree BEFORE leaving".
              beforeSignOut?.();
              void auth.signoutRedirect();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * SignInFailedPage — shown when the OIDC provider reports that a sign-in attempt failed.
 *
 * @remarks Exists because the alternative is a retry loop. react-oidc-context's navigator wrapper
 * sets `isLoading: false` on BOTH the error and the close dispatch, so a guard that reads only
 * `isLoading`/`isAuthenticated` sees "settled and still anonymous" and attempts again immediately —
 * which is the loop that reached React's nested-update ceiling and reported error 185 to Sentry.
 * A failed sign-in has to be a dead stop with a way out, not an automatic retry.
 *
 * Reuses the `nrx-auth-forbidden*` class names rather than minting its own: it is the same
 * centred-card shape, and `stylesheet-contract.spec.tsx` requires a rule for every class rendered.
 */
export function SignInFailedPage(): JSX.Element {
  return (
    <div role="main" className="nrx-auth-centered">
      <div className="nrx-auth-forbidden">
        <h1 className="nrx-auth-forbidden__title">Sign-in failed</h1>
        <p className="nrx-auth-forbidden__body">
          We couldn&apos;t complete sign-in. This is usually temporary.
          <br />
          Contact{' '}
          <a href="mailto:support@nourishedrx.com" className="nrx-auth-forbidden__link">
            NourishedRx support
          </a>{' '}
          if it keeps happening.
        </p>
        <div className="nrx-auth-forbidden__actions">
          <button
            type="button"
            className="nrx-auth-forbidden__signout"
            // A document reload, not a second `signinRedirect`: the attempt latch below is
            // per-mount, so a fresh document is what legitimately re-arms it. User-initiated, so
            // this cannot become the automatic retry the latch exists to prevent.
            onClick={() => {
              window.location.reload();
            }}
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The `auth.error` sources that mean signing IN failed — that field carries eleven provenances, and
 * only these two leave the user with no session and nothing in flight. A failed renew
 * (`renewSilent` from the provider's timer, `signinSilent` from a page driving its own re-auth
 * prompt) still has a working session behind it, so widening this list ejects a signed-in user from
 * the page they are working in.
 */
const SIGN_IN_FAILURE_SOURCES: readonly ErrorContext['source'][] = [
  'signinRedirect',
  'signinCallback',
];

function signInFailed(auth: AuthContextProps): boolean {
  return (
    auth.error !== undefined &&
    !auth.isAuthenticated &&
    SIGN_IN_FAILURE_SOURCES.includes(auth.error.source)
  );
}

/**
 * RequireAuth — React Router v7 layout route guard.
 *
 * Behaviour:
 * - isLoading → renders LoadingSpinner
 * - a FAILED SIGN-IN (see signInFailed) → renders SignInFailedPage, never retries
 * - !isAuthenticated → attempts signinRedirect ONCE per mount with returnTo state, renders null
 * - requiredRolePrefix set → user must have at least one group starting with prefix; else ForbiddenPage
 * - requiredRoles set (and no prefix) → user must hold at least one of them; else ForbiddenPage
 * - requiredRole set (and neither of the above) → user must have exact role; else ForbiddenPage
 * - none set → any authenticated user passes
 * - all checks pass → renders Outlet
 *
 * `returnTo` is the DOCUMENT path (`window.location.pathname`), not `useLocation().pathname`.
 * Under a React Router `basename` the latter strips the suite slug — so a cold deep link into
 * `/food-ops/service-areas` would hand off to `/service-areas` after the OIDC round-trip and
 * land nowhere. AuthCallback drives a document navigation, so the slug must survive.
 */
export function RequireAuth({
  requiredRole,
  requiredRolePrefix,
  requiredRoles,
  pending,
  beforeSignOut,
}: RequireAuthProps): JSX.Element | null {
  const auth = useAuth();

  /**
   * One attempt per mount.
   *
   * The redirect used to be fired from the render body, which made it re-fire on every render that
   * saw "settled and still anonymous". react-oidc-context wraps each navigator method so that
   * `NAVIGATOR_INIT` sets `isLoading: true` and BOTH the `ERROR` and the unconditional
   * `NAVIGATOR_CLOSE` in its `finally` set it back to `false` — so every settled attempt handed the
   * guard the exact state that triggers another one. Each turn was a nested React update, and at 51
   * `getRootForUpdatedFiber` threw error 185 from the wrapper's own synchronous dispatch, which made
   * it surface as an unhandled REJECTION rather than a render error.
   *
   * The gates below are `useAutoSignin`'s, minus `!hasAuthParams()` and plus the failed-sign-in
   * check. Dropping that one is safe only while no app mounts this guard on a route that receives
   * the OIDC `code`/`state` — every app routes the callback as a sibling of it, not under it.
   * `auth` is a new identity on any provider state change, so the latch must stay the FIRST
   * statement — it is what keeps the extra runs inert.
   */
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    if (auth.isLoading || auth.isAuthenticated || auth.activeNavigator) return;
    if (signInFailed(auth)) return;
    attempted.current = true;
    // Document path, not React Router's basename-stripped pathname. Pantry tenants mount with
    // `basename: '/<slug>'`, so `useLocation().pathname` is `/` and AuthCallback would send the
    // browser to the launcher home after the shared `/callback` exchange.
    void auth
      .signinRedirect({
        state: { returnTo: window.location.pathname + window.location.search },
      })
      .catch(() => {
        // The navigator wrapper already catches everything the underlying UserManager can throw and
        // reports it through `auth.error`, so the only rejection that reaches here is a throw from
        // its own synchronous dispatch — which is what error 185 was. The latch above removes that
        // trigger; this keeps a recurrence by some other route from escaping as an unhandled
        // rejection. Deliberately unpinned by a test: vitest mock functions never surface a voided
        // rejection to a process listener, so any test claiming to cover this line would pass
        // whether or not the line existed.
      });
  }, [auth]);

  if (auth.isLoading) {
    return <>{pending ?? <LoadingSpinner />}</>;
  }

  // Before the isAuthenticated branch: a failed attempt leaves the user anonymous, and falling
  // through to that branch is what made this a loop.
  if (signInFailed(auth)) {
    return <SignInFailedPage />;
  }

  if (!auth.isAuthenticated) {
    return null;
  }

  const groups = auth.user?.profile['cognito:groups'];
  const roles: string[] = Array.isArray(groups)
    ? groups.filter((g): g is string => typeof g === 'string')
    : [];

  if (requiredRolePrefix) {
    if (!roles.some((r) => r.startsWith(requiredRolePrefix))) {
      return <ForbiddenPage beforeSignOut={beforeSignOut} />;
    }
  } else if (requiredRoles) {
    // Fails closed on an empty list — an app that computes its gate from a manifest must not
    // silently open up if that manifest arrives empty.
    if (!roles.some((r) => requiredRoles.includes(r))) {
      return <ForbiddenPage beforeSignOut={beforeSignOut} />;
    }
  } else if (requiredRole) {
    if (!roles.includes(requiredRole)) {
      return <ForbiddenPage beforeSignOut={beforeSignOut} />;
    }
  }

  return <Outlet />;
}
